(function () {
  'use strict';
  if (window.MWStorefrontContentRecords) return;

  const ID = /^[a-z0-9][a-z0-9_-]{0,79}$/i;
  const ALIAS = /^[a-z0-9][a-z0-9:._-]{0,199}$/i;
  const PRODUCT = /^gid:\/\/shopify\/Product\/[1-9][0-9]*$/;
  const KINDS = ['artist', 'quote', 'quoteCollection', 'demo', 'audioSet', 'placement'];
  const MARKS = ['bold', 'italic', 'small'];
  const encoder = new TextEncoder();
  const own = (object, key) => Object.prototype.hasOwnProperty.call(object, key);
  const fail = () => { throw new Error('Published storefront content is invalid or incomplete.'); };
  const escapeHTML = value => value.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;').replace(/'/g, '&#39;');

  function object(value, required, optional = []) {
    if (!value || typeof value !== 'object' || ![Object.prototype, null].includes(Object.getPrototypeOf(value))) fail();
    const allowed = new Set([...required, ...optional]);
    for (const key of Reflect.ownKeys(value)) {
      const descriptor = Object.getOwnPropertyDescriptor(value, key);
      if (typeof key !== 'string' || !allowed.has(key) || !descriptor.enumerable || !own(descriptor, 'value')) fail();
    }
    if (required.some(key => !own(value, key))) fail();
    return value;
  }

  function list(value, limit = 1000) {
    if (!Array.isArray(value) || Object.getPrototypeOf(value) !== Array.prototype || value.length > limit || Reflect.ownKeys(value).length !== value.length + 1) fail();
    for (let i = 0; i < value.length; i++) {
      const descriptor = Object.getOwnPropertyDescriptor(value, String(i));
      if (!descriptor || !descriptor.enumerable || !own(descriptor, 'value')) fail();
    }
    return value;
  }

  function text(value, limit = 4096, empty = false) {
    if (typeof value !== 'string' || value.length > limit || encoder.encode(value).length > limit || (!empty && !value.trim())
      || /[\u0000-\u0008\u000b\u000c\u000e-\u001f\u007f]/u.test(value)
      || /[\uD800-\uDBFF](?![\uDC00-\uDFFF])|(?<![\uD800-\uDBFF])[\uDC00-\uDFFF]/u.test(value)) fail();
    return value;
  }

  function identity(value, alias = false) { if (typeof value !== 'string' || !(alias ? ALIAS : ID).test(value)) fail(); return value; }
  function unique(values) { if (new Set(values).size !== values.length) fail(); return values; }
  function ids(values) { return unique(list(values).map(value => identity(value))); }
  function order(source, result) { if (own(source, 'order')) { if (!Number.isSafeInteger(source.order) || source.order < 0) fail(); result.order = source.order; } return result; }
  function flags(source, result, keys) { for (const key of keys) if (own(source, key)) { if (typeof source[key] !== 'boolean') fail(); result[key] = source[key]; } return result; }
  function mode(value) { if (!['simple', 'comparison'].includes(value)) fail(); return value; }
  function freeze(value) { if (value && typeof value === 'object' && !Object.isFrozen(value)) { Object.values(value).forEach(freeze); Object.freeze(value); } return value; }

  function https(value, media = false) {
    text(value);
    if (!/^https:\/\//i.test(value) || /\s|\\/u.test(value)) fail();
    let parsed; try { parsed = new URL(value); } catch { fail(); }
    if (parsed.protocol !== 'https:' || !parsed.hostname || parsed.username || parsed.password || /^https:\/\/[^/?#]*@/i.test(value) || parsed.href.length > 4096) fail();
    if (media && (parsed.hostname !== 'cdn.shopify.com' || parsed.port || value.includes('#'))) fail();
    return parsed.href;
  }

  function readMore(value) {
    text(value);
    if (value.startsWith('#')) { identity(value.slice(1), true); return value; }
    if (value.startsWith('/')) {
      if (value.startsWith('//') || /\s|\\|[<>"'`]/u.test(value)) fail();
      let parsed; try { parsed = new URL(value, 'https://content.invalid'); } catch { fail(); }
      if (parsed.origin !== 'https://content.invalid') fail();
      const path = `${parsed.pathname}${parsed.search}${parsed.hash}`; text(path); return path;
    }
    return https(value);
  }

  function body(nodes) {
    let bytes = 0, visible = false;
    const result = list(nodes, 256).map(node => {
      object(node, ['type'], ['text', 'href', 'newTab', ...MARKS]);
      if (node.type === 'break') { object(node, ['type']); return { type: 'break' }; }
      if (!['text', 'link'].includes(node.type)) fail();
      object(node, ['type', 'text', ...(node.type === 'link' ? ['href'] : [])], [...MARKS, ...(node.type === 'link' ? ['newTab'] : [])]);
      const result = flags(node, { type: node.type, text: text(node.text, 16 * 1024, node.type === 'text') }, MARKS);
      bytes += encoder.encode(result.text).length; visible ||= Boolean(result.text.trim());
      if (node.type === 'link') { result.href = https(node.href); flags(node, result, ['newTab']); }
      return result;
    });
    if (!visible || bytes > 16 * 1024) fail();
    return result;
  }

  function normalize(source) {
    object(source, ['kind', 'id'], ['legacyAliases', 'legacyId', 'name', 'image', 'credits', 'artistId', 'quoteCollectionId', 'body', 'featured', 'order', 'title', 'anchorAliases', 'quoteIds', 'logo', 'productHandle', 'gridId', 'readMoreURL', 'addedAt', 'mode', 'media', 'items', 'destination', 'quoteCollectionIds', 'audioSetIds', 'cardAudioSetId', 'featureAudioSetIds']);
    if (!KINDS.includes(source.kind)) fail();
    const base = { kind: source.kind, id: identity(source.id) };
    if (own(source, 'legacyAliases')) base.legacyAliases = unique(list(source.legacyAliases, 200).map(value => identity(value, true)));
    if (source.kind === 'artist') {
      object(source, ['kind', 'id', 'name', 'image', 'credits'], ['legacyAliases', 'order']);
      return order(source, { ...base, name: text(source.name, 256), image: source.image === null ? null : https(source.image, true), credits: text(source.credits, 4096, true) });
    }
    if (source.kind === 'quote') {
      object(source, ['kind', 'id', 'artistId', 'body'], ['legacyAliases', 'legacyId', 'quoteCollectionId', 'featured', 'order']);
      const result = order(source, flags(source, { ...base, artistId: identity(source.artistId), body: body(source.body) }, ['featured']));
      if (own(source, 'quoteCollectionId')) result.quoteCollectionId = identity(source.quoteCollectionId);
      if (own(source, 'legacyId')) result.legacyId = identity(source.legacyId, true);
      return result;
    }
    if (source.kind === 'quoteCollection') {
      object(source, ['kind', 'id', 'title', 'anchorAliases', 'quoteIds'], ['legacyAliases', 'logo', 'productHandle', 'legacyId', 'gridId', 'readMoreURL', 'order', 'addedAt']);
      const result = order(source, { ...base, title: text(source.title, 256), anchorAliases: ids(source.anchorAliases), quoteIds: ids(source.quoteIds) });
      if (own(source, 'addedAt')) {
        if (typeof source.addedAt !== 'string' || !Number.isFinite(Date.parse(source.addedAt)) || new Date(source.addedAt).toISOString() !== source.addedAt) fail();
        result.addedAt = source.addedAt;
      }
      if (own(source, 'logo')) result.logo = source.logo === null ? null : https(source.logo, true);
      if (own(source, 'productHandle')) { if (source.productHandle !== null && (typeof source.productHandle !== 'string' || !/^[a-z0-9][a-z0-9-]{0,199}$/i.test(source.productHandle))) fail(); result.productHandle = source.productHandle; }
      for (const key of ['legacyId', 'gridId']) if (own(source, key)) result[key] = identity(source[key], true);
      if (own(source, 'readMoreURL')) result.readMoreURL = readMore(source.readMoreURL);
      return result;
    }
    if (source.kind === 'demo') {
      object(source, ['kind', 'id', 'mode', 'media'], ['legacyAliases']);
      const audioMode = mode(source.mode);
      if (audioMode === 'simple') { object(source.media, ['url']); return { ...base, mode: audioMode, media: { url: https(source.media.url, true) } }; }
      object(source.media, ['on', 'off'], ['video']);
      const media = { on: https(source.media.on, true), off: https(source.media.off, true) };
      if (own(source.media, 'video')) media.video = https(source.media.video, true);
      if (media.on === media.off) fail();
      return { ...base, mode: audioMode, media };
    }
    if (source.kind === 'audioSet') {
      object(source, ['kind', 'id', 'title', 'mode', 'items'], ['legacyAliases']);
      const items = list(source.items).map(item => {
        object(item, ['demoId', 'title', 'artist'], ['id']);
        const result = { demoId: identity(item.demoId), title: text(item.title, 256), artist: text(item.artist, 256, true) };
        if (own(item, 'id')) result.id = identity(item.id);
        return result;
      });
      unique(items.filter(item => own(item, 'id')).map(item => item.id));
      const uses = new Map();
      for (const item of items) { const same = uses.get(item.demoId) || []; same.push(item); uses.set(item.demoId, same); }
      for (const same of uses.values()) if (same.length > 1 && same.some(item => !own(item, 'id'))) fail();
      return { ...base, title: text(source.title, 256), mode: mode(source.mode), items };
    }
    object(source, ['kind', 'id', 'destination', 'quoteCollectionIds', 'audioSetIds', 'cardAudioSetId'], ['legacyAliases', 'featureAudioSetIds']);
    object(source.destination, ['type'], ['productId', 'key']);
    let destination;
    if (source.destination.type === 'product') {
      object(source.destination, ['type', 'productId']);
      if (typeof source.destination.productId !== 'string' || source.destination.productId.length > 128 || !PRODUCT.test(source.destination.productId)) fail();
      destination = { type: 'product', productId: source.destination.productId };
    } else {
      object(source.destination, ['type', 'key']);
      if (source.destination.type !== 'page' || source.destination.key !== 'artists') fail();
      destination = { type: 'page', key: 'artists' };
    }
    const result = { ...base, destination, quoteCollectionIds: ids(source.quoteCollectionIds), audioSetIds: ids(source.audioSetIds), cardAudioSetId: source.cardAudioSetId === null ? null : identity(source.cardAudioSetId) };
    if (result.cardAudioSetId !== null && (destination.type !== 'product' || !result.audioSetIds.includes(result.cardAudioSetId))) fail();
    if (own(source, 'featureAudioSetIds')) {
      if (destination.type !== 'product') fail();
      result.featureAudioSetIds = ids(source.featureAudioSetIds);
    }
    return result;
  }

  function quoteMarkup(nodes) {
    return nodes.map(node => {
      if (node.type === 'break') return '<br>';
      let result = escapeHTML(node.text);
      if (node.bold) result = `<strong>${result}</strong>`;
      if (node.italic) result = `<em>${result}</em>`;
      if (node.small) result = `<small>${result}</small>`;
      if (node.type === 'link') result = `<a href="${escapeHTML(node.href)}"${node.newTab ? ' target="_blank" rel="noopener noreferrer"' : ''}>${result}</a>`;
      return result;
    }).join('');
  }

  function create(input) {
    const records = list(input, 10000).map(normalize), byKey = new Map(), aliases = new Map(), destinations = new Map(), membership = new Map();
    const key = (kind, id) => `${kind}:${id}`;
    const requireRecord = (kind, id) => { const record = byKey.get(key(kind, id)); if (!record) fail(); return record; };
    for (const record of records) {
      const typedKey = key(record.kind, record.id); if (byKey.has(typedKey)) fail(); byKey.set(typedKey, record);
      if (record.kind === 'placement') {
        const destination = record.destination.type === 'product' ? record.destination.productId : `page:${record.destination.key}`;
        if (destinations.has(destination)) fail(); destinations.set(destination, record);
      }
    }
    for (const record of records) {
      const namedAliases = [...(record.legacyAliases || []), ...(record.legacyId ? [record.legacyId] : []), ...(record.kind === 'quoteCollection' ? record.anchorAliases : [])];
      for (const alias of new Set(namedAliases)) {
        const typedAlias = key(record.kind, alias), collision = byKey.get(typedAlias) || aliases.get(typedAlias);
        if (collision && collision !== record) fail(); aliases.set(typedAlias, record);
      }
      if (record.kind === 'quoteCollection') {
        for (const quoteId of record.quoteIds) {
          requireRecord('quote', quoteId);
          const collections = membership.get(quoteId) || []; collections.push(record); membership.set(quoteId, collections);
        }
      }
      if (record.kind === 'audioSet') for (const item of record.items) if (requireRecord('demo', item.demoId).mode !== record.mode) fail();
      if (record.kind === 'placement') {
        const seen = new Set();
        for (const id of record.quoteCollectionIds) {
          const collection = requireRecord('quoteCollection', id);
          const ownAliases = new Set([...collection.anchorAliases, collection.legacyId || collection.id]);
          for (const alias of ownAliases) { if (seen.has(alias)) fail(); seen.add(alias); }
        }
        for (const id of record.audioSetIds) {
          const set = requireRecord('audioSet', id);
          if (set.mode === 'comparison' && set.items.some(item => !requireRecord('demo', item.demoId).media.video)) fail();
        }
        for (const id of record.featureAudioSetIds || []) if (requireRecord('audioSet', id).mode !== 'comparison') fail();
      }
    }
    const origins = new Map();
    for (const record of records) if (record.kind === 'quote') {
      requireRecord('artist', record.artistId);
      const collections = membership.get(record.id) || [];
      const origin = record.quoteCollectionId ? requireRecord('quoteCollection', record.quoteCollectionId) : collections.length === 1 ? collections[0] : null;
      if (!origin || !origin.quoteIds.includes(record.id)) fail(); origins.set(record.id, origin);
    }
    records.forEach(freeze);

    const find = (kind, id) => {
      if (!KINDS.includes(kind)) fail(); identity(id, true);
      return byKey.get(key(kind, id)) || aliases.get(key(kind, id)) || null;
    };
    const quoteViews = new Map(), collectionViews = new Map(), audioViews = new Map();
    function resolveQuote(record) {
      if (quoteViews.has(record.id)) return quoteViews.get(record.id);
      const artist = requireRecord('artist', record.artistId), topic = origins.get(record.id);
      const link = topic.readMoreURL || (topic.anchorAliases.length ? `#${topic.anchorAliases[0]}` : `#${topic.legacyId || topic.id}`);
      const plain = { name: artist.name, image: artist.image || '', credits: artist.credits, productName: topic.title, productLogo: topic.logo || '', productURL: topic.productHandle || '', readMoreURL: link, readMoreHref: link.startsWith('#') ? `/pages/artists${link}` : link };
      const html = Object.fromEntries(Object.entries(plain).map(([name, value]) => [name, escapeHTML(value)]));
      const quoteHtml = quoteMarkup(record.body);
      const result = freeze({ id: record.id, artistId: record.artistId, quoteCollectionId: topic.id, order: record.order ?? null, featured: record.featured ?? false,
        ...html, quote: quoteHtml, quoteHtml, quoteText: record.body.map(node => node.type === 'break' ? '\n' : node.text).join(''), body: record.body, plain });
      quoteViews.set(record.id, result); return result;
    }
    function resolveCollection(record) {
      if (collectionViews.has(record.id)) return collectionViews.get(record.id);
      const link = record.readMoreURL || `#${record.anchorAliases[0] || record.legacyId || record.id}`;
      const plain = { title: record.title, logo: record.logo || '', productHandle: record.productHandle || '', productHref: record.productHandle ? `/products/${record.productHandle}` : '', readMoreURL: link, readMoreHref: link.startsWith('#') ? `/pages/artists${link}` : link };
      const result = freeze({ id: record.id, filterId: record.legacyId || record.id, gridId: record.gridId || `${record.id}Grid`, anchorAliases: record.anchorAliases,
        legacyAliases: record.legacyAliases || [], order: record.order ?? null, addedAt: record.addedAt ?? null, ...Object.fromEntries(Object.entries(plain).map(([name, value]) => [name, escapeHTML(value)])), plain,
        quotes: record.quoteIds.map(id => resolveQuote(requireRecord('quote', id))) });
      collectionViews.set(record.id, result); return result;
    }
    function resolveAudio(record) {
      if (audioViews.has(record.id)) return audioViews.get(record.id);
      const tracks = record.items.map(item => {
        const demo = requireRecord('demo', item.demoId);
        return { id: item.id || null, demoId: demo.id, title: item.title, artist: item.artist, titleHtml: escapeHTML(item.title), artistHtml: escapeHTML(item.artist),
          ...(record.mode === 'comparison' ? { songTitle: item.title, songTitleHtml: escapeHTML(item.title) } : {}), ...demo.media };
      });
      const result = freeze({ id: record.id, title: record.title, titleHtml: escapeHTML(record.title), mode: record.mode, type: record.mode, tracks });
      audioViews.set(record.id, result); return result;
    }
    function quoteSelection(placement, gallery = false) {
      if (!placement) return freeze({ managed: false, placementId: null, collections: [], quotes: [] });
      let collections = placement.quoteCollectionIds.map(id => resolveCollection(requireRecord('quoteCollection', id)));
      if (gallery) collections = collections.slice().sort((a, b) => {
        if (a.addedAt && b.addedAt) {
          const recentFirst = Date.parse(b.addedAt) - Date.parse(a.addedAt);
          if (recentFirst) return recentFirst;
        } else if (a.addedAt || b.addedAt) return a.addedAt ? -1 : 1;
        return (a.order ?? Number.MAX_SAFE_INTEGER) - (b.order ?? Number.MAX_SAFE_INTEGER) || a.id.localeCompare(b.id);
      });
      const used = new Set(), quotes = [];
      for (const collection of collections) for (const quote of collection.quotes) if (!used.has(quote.id)) { used.add(quote.id); quotes.push(quote); }
      if (gallery) quotes.sort((a, b) => {
        const first = requireRecord('quoteCollection', a.quoteCollectionId).addedAt;
        const second = requireRecord('quoteCollection', b.quoteCollectionId).addedAt;
        if (first && second) {
          const recentFirst = Date.parse(second) - Date.parse(first);
          if (recentFirst) return recentFirst;
        } else if (first || second) return first ? -1 : 1;
        return (a.order ?? Number.MAX_SAFE_INTEGER) - (b.order ?? Number.MAX_SAFE_INTEGER);
      });
      return freeze({ managed: true, placementId: placement.id, collections, quotes });
    }
    function productPlacement(productId) { if (typeof productId !== 'string' || !PRODUCT.test(productId) || productId.length > 128) fail(); return destinations.get(productId); }
    return Object.freeze({
      record: find,
      all(kind) { if (!KINDS.includes(kind)) fail(); return Object.freeze(records.filter(record => record.kind === kind)); },
      quote(id) { const value = find('quote', id); return value ? resolveQuote(value) : null; },
      quoteCollection(id) { const value = find('quoteCollection', id); return value ? resolveCollection(value) : null; },
      productQuotes(productId) { return quoteSelection(productPlacement(productId)); },
      productAudio(productId) { const placement = productPlacement(productId); return freeze({ managed: Boolean(placement), placementId: placement?.id || null, sets: placement ? placement.audioSetIds.map(id => resolveAudio(requireRecord('audioSet', id))) : [] }); },
      cardAudio(productId) { const placement = productPlacement(productId); return freeze({ managed: Boolean(placement), placementId: placement?.id || null, audio: placement?.cardAudioSetId ? resolveAudio(requireRecord('audioSet', placement.cardAudioSetId)) : null }); },
      productFeatureAudio(productId, audioSetId) {
        const placement = productPlacement(productId);
        identity(audioSetId);
        const assigned = placement?.featureAudioSetIds?.includes(audioSetId);
        return freeze({ managed: Boolean(placement), placementId: placement?.id || null, audio: assigned ? resolveAudio(requireRecord('audioSet', audioSetId)) : null });
      },
      gallery(pageKey = 'artists') {
        if (pageKey !== 'artists') fail();
        const page = destinations.get(`page:${pageKey}`);
        const galleryPlacement = page ? { ...page, quoteCollectionIds: records.filter(record => record.kind === 'quoteCollection').map(record => record.id) } : null;
        const selection = quoteSelection(galleryPlacement, true), artists = new Map();
        for (const quote of selection.quotes) {
          if (!artists.has(quote.artistId)) artists.set(quote.artistId, { id: quote.artistId, name: quote.name, image: quote.image, credits: quote.credits,
            plain: { name: quote.plain.name, image: quote.plain.image, credits: quote.plain.credits }, quotes: [] });
          artists.get(quote.artistId).quotes.push(quote);
        }
        return freeze({ ...selection, artists: [...artists.values()] });
      },
    });
  }

  window.MWStorefrontContentRecords = Object.freeze({ create, escapeHTML });
})();
